<template>
  <div
    class="submit-tender clearfix"
    style="display: inline-block"
  >
    <button
      :disabled="isUnderEditing"
      type="button"
      id="submit_tender_addendum"
      :class="className"
      @click="init()"
    >{{ $t('action.submit') }}</button>

    <div
      class="reveal choose-approver"
      ref="chooseApprModal"
      :id="chooseApprModalId"
    >

      <div class="modal-container">
        <p class="confirm-header">{{ $t('tender.submit.popupTitle') }}?</p>
        <div class="clearfix">
          <div class="tender-font float-left clearfix">{{ $t('tender.submit.chooseApprover') }}</div>
        </div>

        <HKLSelect
          class="submit-hkl-select"
          :value="approverId || undefined"
          :options="approverHklOptions"
          :showSearch="true"
          placeholder=""
          @change="onApproverChange"
        />
        <span
          class="form-error is-visible appr-error-message"
          v-if="apprErrHint"
        >
          {{ apprErrHint }}
        </span>
      </div>

      <div class="row align-right nav_btn">
        <button
          type="button"
          class="btn btn--darkblue"
          aria-label="Close reveal"
          @click="closeApprModal()"
        >{{ $t('action.cancel') }}</button>
        <button
          type="submit"
          class="btn btn--orange"
          @click="selectApprover()"
        >{{ $t('action.continue') }}</button>
      </div>
    </div>

    <div
      class="reveal alertModalId_tender_approval"
      ref="summaryModal"
      :id="summaryModalId"
    >
      <div class="flex-box">
        <div class="modal-container flex-box-content">
          <p class="confirm-header"> {{ $t('warn.addendum.confirmTitle') }} </p>
          <p> {{ $t('warn.addendum.confirmDesc', [ selectedApproverName ]) }} </p>
          <addendum-item :addendums="addendum" />
        </div>
        <div class="flex-box-footer row align-right nav_btn">
          <button
            type="button"
            class="btn btn--darkblue"
            aria-label="Close reveal"
            @click="closeSummaryModal()"
          >{{ $t('action.cancel') }}</button>
          <button
            type="submit"
            class="btn btn--orange"
            @click="submitTender()"
          >{{ $t('action.submit') }}</button>
        </div>
      </div>
    </div>

    <Modal
      :modalId="alertModalId"
      ref="alertModal"
    >
      <div slot="content">
        <p class="modal-pure-text">{{ $t('tender.submit.submitted') }}</p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="navHome()"
        >{{ $t('hint.okay') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Modal from '@/controls/Modal'
import router from '@/router'
import AddendumItem from '@/modules/tenders/review/AddendumItem'
import { getApprovers } from '@/api/users/request'
import { diffAllVersions } from '@/api/tender-addendums/request'
import { tenderStatus } from '@/utils/locale'
import { EmailTypes, ActionTypes } from '@/utils/consts'
import { sendEmail } from '@/api/postman/request'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import navigation from '@/utils/navigation'
import lodash from 'lodash'

export default {
  name: 'SubmitAddendum',
  components: {
    HKLSelect,
    AddendumItem,
    Modal,
  },

  props: {
    isUnderEditing: {
      type: Boolean,
      default: false,
    },
    clickCheck: {
      type: Function,
      default: () => { return true },
    },
    className: {
      type: String,
      default: 'btn btn--orange',
    },
    validateClosingDate: {
      type: Function,
      default: {},
    },
  },

  data () {
    const generalMandatoryFields = [
      {code: 'projectDescription', name: 'Project Description', isArray: false},
      {code: 'projectLocation', name: 'Project Location', isArray: false},
      {code: 'projectStart', name: 'Tentative Project Start Date', isArray: false},
      {code: 'estimatedProjectDuration', name: 'Estimated Project Duration', isArray: false},
      // {code: 'closingDate', name: 'Closing Date', isArray: false},
      // {code: 'workingHours', name: 'Working Hours', isArray: true}.
      {code: 'validityPeriod', name: 'Validity Period', isArray: false}]
    return {
      generalMandatoryFields,
      apprErrHint: '',
      approverId: null,
      options: [],
      addendums: [],
      alertModalId: `alertModal-${this.getRandomIdSuffix()}`,
      chooseApprModalId: `chooseApprModal-${this.getRandomIdSuffix()}`,
      summaryModalId: `summaryModa-${this.getRandomIdSuffix()}`,
    }
  },

  beforeDestroyed () {
    if (this.alertModal) {
      this.alertModal.destroy()
    }
    if (this.apprModal) {
      this.apprModal.destroy()
    }
  },

  methods: {
    ...mapActions('tenders', ['saveCurrentTender', 'updateTenderStatus', 'refreshTender']),
    ...mapMutations('tenders', {
      updateTenderByPath: mutationTypes.TENDER_UPDATE_PATH_FIELD,
    }),
    getRandomIdSuffix () {
      return `${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    },
    validateMandatoryFields () {
      let emptyfields = []
      if (this.currentTender.general.noisyWorkingHoursChecked) {
        this.generalMandatoryFields.push({code: 'noisyWorkingHours', name: 'Noisy Working Hours', isArray: true})
      }
      for (const item in this.generalMandatoryFields) {
        const flag = !this.generalMandatoryFields[item].isArray ? !this.currentTender.general[this.generalMandatoryFields[item].code] : this.currentTender.general[this.generalMandatoryFields[item].code].length === 0
        if (flag) {
          emptyfields.push(this.generalMandatoryFields[item].name)
        }
      }
      // validate Optional Items
      if (this.currentTender.general.optionalItems.length > 0) {
        const optionalItemsArray = this.currentTender.general.optionalItems
        for (let i = 0; i < optionalItemsArray.length; i++) {
          if (optionalItemsArray[i].selected && !optionalItemsArray[i].value) {
            emptyfields.push('Additional Items')
            break
          }
        }
      }
      // validate Item Service
      let serviceItems = this.currentTender.service
      let serviceCounter = 0
      for (let i = 0; i < serviceItems.length; i++) {
        if (!(serviceItems[i].addendumLabel && serviceItems[i].addendumLabel.startsWith('D'))) {
          serviceCounter++
        }
      }
      if (serviceCounter === 0) {
        emptyfields.push(' Item / Service')
      }
      return emptyfields
    },
    async init () {
      const checkResult = await this.clickCheck()
      if (!checkResult) return
      const emptyfields = this.validateMandatoryFields()
      if (emptyfields.length > 0) {
        const alertMsg = this.$i18n.t('tender.alertMsg') + String(emptyfields)
        notification.fireNotification(false, alertMsg)
        return
      }
      if (!this.alertModal) {
        this.alertModal = this.$refs.alertModal
      }
      if (!this.apprModal) {
        this.apprModal = await new Foundation.Reveal($(`#${this.chooseApprModalId}`), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
      if (!this.summaryModal) {
        this.summaryModal = await new Foundation.Reveal($(`#${this.summaryModalId}`), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
      this.openApprModal()
    },
    openApprModal () {
      this.apprModal.open()
    },
    openAlertModal () {
      this.alertModal.open()
    },
    openSummaryModal () {
      this.summaryModal.open()
    },
    closeAlertModal () {
      this.alertModal.close()
    },
    closeApprModal () {
      this.apprModal.close()
    },
    closeSummaryModal () {
      this.summaryModal.close()
    },
    onApproverChange (id) {
      this.approverId = id || null
      if (this.apprErrHint) {
        this.apprErrHint = ''
      }
    },
    isValidApprover () {
      return this.approverId && (this.options.some(option => option.id === this.approverId))
    },
    async openSummary () {
      this.addendums =
        await diffAllVersions(this.tenderId || this.$router.currentRoute.params.id)

      this.openSummaryModal()
    },
    async selectApprover () {
      if (this.isValidApprover()) {
        this.closeApprModal()
        await this.openSummary()
      } else {
        this.apprErrHint = this.$i18n.t('tender.submit.apprRequired')
      }
    },
    async submit (status, action, emailType) {
      await this.saveCurrentTender({ showNotification: false, updatedBidStatus: false })

      const id = router.currentRoute.params.id
      await this.updateTenderStatus({
        id,
        status,
        action,
      })

      await this.refreshTender()
      await sendEmail(emailType)
      this.closeSummaryModal()
    },

    async submitTender () {
      try {
        if (this.isValidApprover()) {
          this.updateTenderByPath({
            path: ['general', 'approvedBy'],
            newValue: this.approverId,
          })

          await this.submit(tenderStatus.WAPPR, ActionTypes.tenders.addendum.submit, EmailTypes.tenders.submitForApproval)
          this.openAlertModal()
        } else {
          this.apprErrHint = this.$i18n.t('tender.submit.apprRequired')
        }
      } catch (err) {
        notification.handleResponseError(err)
      }
    },

    navHome () {
      this.closeAlertModal()
      navigation.toHome()
    },
  },
  computed: {
    ...mapGetters('tenders', ['currentTender']),
    approverHklOptions () {
      return (this.options || []).map((opt) => ({
        name: opt.name,
        value: opt.id,
      }))
    },
    selectedApproverName () {
      if (!this.approverId) {
        return ''
      }
      const found = this.options.find((opt) => opt.id === this.approverId)
      return found ? found.name : ''
    },
    addendum () {
      if (this.addendums[0]) {
        return this.addendums[0].addendum
      }
      return []
    },
  },
  async mounted () {
    const approvers = await getApprovers()
    this.options = lodash.cloneDeep(approvers)
      .sort((a, b) => a.name.localeCompare(b.name))
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/popup';
@import "~styles/stickyBtn";

#submit_tender_addendum {
  margin-left: 12px;
}
</style>

<style lang="scss" scoped>
.alertModalId_tender_approval {
  width: 500px;
  p {
    font-weight: bold;
  }
  div:last-child {
    padding-top: 10px;
  }
}
#summaryModal {
  width: 700px!important;
}
.flex-box{
  position: relative;
  box-sizing: border-box;
  padding-bottom: 60px;
  max-height: calc( 100vh - 250px);
}
.flex-box-content{
  max-height: calc( 100vh - 310px);
  overflow-y: auto;
  pre{
    overflow-x: hidden;
  }
}
.flex-box-footer{
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding-top: 10px;
}
</style>
